// Who this sign-in is, in the terms the reader would use about themselves.
//
// ITS OWN MODULE BECAUSE THREE SCREENS ASK NOW. The top bar's avatar and menu
// want it, and so does the account page, and the failure of leaving it in
// Chrome.tsx was going to be the account page growing a second spelling of the
// same sentence. Two descriptions of one role that drift apart is a console
// that tells somebody they are one thing in the menu and another on the page
// the menu opens.
//
// EVERYTHING HERE IS DRAWN FROM `role` AND `clientId`, WHICH THE SERVER SENT
// BECAUSE THE CALLER HAD JUST PROVED THEY HOLD THE ADDRESS — so it tells them
// nothing they did not establish. It decides what is written and it decides
// nothing about access: every read is answered by a server that re-derives the
// role from the session. A console that treated these strings as authority
// would be a console where editing one character of sessionStorage changed what
// it tried to do, and the interesting part is that it would still be refused,
// one layer down, by 004.

import type { Session } from "../session/store.js";

/**
 * The role and the reach, apart.
 *
 * TWO FIELDS RATHER THAN ONE SENTENCE, because the account page sets them in a
 * table with a row each and the menu sets them on one line. Composing the line
 * from these is cheap; taking a sentence apart again is a regular expression
 * over a string somebody may translate.
 *
 * A role this build does not recognise is printed as itself rather than as
 * "unknown". It means the service is ahead of this bundle, and the honest thing
 * on a screen is the word the server used. Its scope is null rather than
 * guessed: a bundle that does not know the role cannot know what it reaches,
 * and writing "every client" beside a word we do not recognise is the one
 * mistake on this screen that would matter.
 */
export function roleAndScope(session: Session): {
  readonly role: string;
  readonly scope: string | null;
} {
  if (session.role === "nova_admin") {
    return { role: "Nova admin", scope: "All clients" };
  }
  if (session.role === "nova_editor") {
    return { role: "Editor", scope: "Given clients" };
  }
  if (session.role === "client_viewer") {
    return {
      role: "Client viewer",
      scope: session.clientId === null ? "One client" : `One client (${session.clientId})`,
    };
  }
  return { role: session.role, scope: null };
}

/**
 * The one-line version, for the menu and the avatar's title.
 *
 * Kept spelling the em dash it has always spelled, because check.mjs and a
 * mutation both watch this string and because the console has said it this way
 * on every screen since the frame was written.
 */
export function describeRole(session: Session): string {
  if (session.role === "nova_admin") return "Nova admin: every client";
  if (session.role === "nova_editor") return "Editor: the clients you are given";
  if (session.role === "client_viewer") {
    return session.clientId === null ? "Client viewer" : `Client viewer: ${session.clientId}`;
  }
  return session.role;
}

/**
 * Two characters for the avatar, from the role rather than from a name.
 *
 * THERE IS NO NAME TO USE. `identity` in the database has an email, a role, a
 * client and two timestamps, and no column for what a person is called;
 * nothing on the sign-in path asks for one and no endpoint would return one.
 * The design pack draws initials from `ME.name`, which is a fixture.
 *
 * AND THE ADDRESS IS NOT A SUBSTITUTE, though it is now on hand and the
 * temptation is real. `gabe@blckpk.com` would give "GA" or, worse, somebody
 * would split the local part on a dot and produce initials for a person whose
 * address is `bookings@`. Putting a guess about somebody's identity on every
 * screen they open is a worse failure than two flat letters that are true.
 */
export function initials(session: Session): string {
  if (session.role === "nova_admin") return "NA";
  if (session.role === "nova_editor") return "ED";
  if (session.role === "client_viewer") return "CV";
  return session.role.slice(0, 2).toUpperCase();
}
